<template>
  <div :class="b({ imagePosition, width, variant })">
    <div
      v-if="mediaType === 'image' && image"
      :class="b('image')"
    >
      <e-picture
        :sizes="sizes"
        :srcset="image.srcset"
        :fallback="image.fallback"
        :alt="image.alt"
        :width="imageInitialWidth"
        :height="imageInitialHeight"
      />
    </div>
    <div
      v-if="mediaType === 'video'"
      :class="b('video-wrapper')"
    >
      <video
        v-if="desktopVideo.path"
        :class="b('video', { device: 'desktop' })"
        :src="desktopVideo.path"
        autoplay
        playsinline
        loop
        muted
      ></video>
      <video
        v-if="finalTabletVideo.path"
        :class="b('video', { device: 'tablet' })"
        :src="finalTabletVideo.path"
        autoplay
        playsinline
        loop
        muted
      ></video>
      <video
        v-if="finalMobileVideo.path"
        :class="b('video', { device: 'mobile' })"
        :src="finalMobileVideo.path"
        autoplay
        playsinline
        loop
        muted
      ></video>
    </div>
    <div :class="b('container')">
      <div :class="b('text-wrapper')">
        <div
          v-if="prefix || date"
          :class="b('prefix')"
        >
          {{ prefixLine }}
        </div>
        <div
          v-if="title"
          :class="b('title')"
        >
          {{ title }}
        </div>
        <slot>
          <template v-if="teaserText">
            <!-- eslint-disable vue/no-v-html -->
            <div
              v-html="teaserText"
              :class="b('teaser-text')"
            ></div>
            <!-- eslint-enable vue/no-v-html -->
          </template>
          <a
            v-if="link && link.url"
            :class="b('link')"
            :href="link.url"
            :target="link.target || '_self'"
            :title="link.title"
          >
            {{ link.name }}
            <e-icon-sprite
              v-if="linkIcon"
              :icon="linkIcon"
              size="16"
            />
          </a>
        </slot>
      </div>
    </div>
    <a
      v-if="overviewLink && overviewLink.url"
      :class="b('overview-link')"
      :href="overviewLink.url"
      :target="overviewLink.target || '_self'"
      :title="overviewLink.title"
    >
      {{ overviewLink.name }}
      <e-icon-sprite
        icon="i-chevron-2--right"
        size="16"
      />
    </a>
  </div>
</template>

<script>
  import eIconSprite from '@/elements/e-icon-sprite.vue';
  import ePicture from '@/elements/e-picture.vue';
  import { formatDate } from '@/helpers/format-date-time.js';

  /**
   * Hero banner component renders an image and content block.
   * All the content parts are optional.
   */
  export default {
    name: 'c-hero-banner',
    components: {
      eIconSprite,
      ePicture,
    },

    // mixins: [],

    props: {
      /**
       * Determines the media type to display. Supported values: `['image', 'video']`.
       * Default is 'image'.
       */
      mediaType: {
        type: String,
        default: 'image',
        validator(value) {
          return ['image', 'video'].includes(value);
        },
      },

      /**
       * Accepts an e-picture conform object.
       */
      image: {
        type: Object,
        required: false,
        default: null,
      },

      /**
       * Video object for the desktop resolution.
       */
      desktopVideo: {
        type: Object,
        required: false,
        default: null,
      },

      /**
       * Video object for the tablet resolution.
       */
      tabletVideo: {
        type: Object,
        required: false,
        default: null,
      },

      /**
       * Video object for the mobile resolution.
       */
      mobileVideo: {
        type: Object,
        required: false,
        default: null,
      },

      /**
       * Defines the position of the image.
       */
      imagePosition: {
        type: String,
        default: 'left',
        validator(value) {
          return ['left', 'right', 'top'].includes(value);
        },
      },

      /**
       * Defines the prefix (category) of the text part.
       */
      prefix: {
        type: String,
        default: null,
      },

      /**
       * Defines the title of the text part.
       */
      title: {
        type: String,
        default: null,
      },

      /**
       * Defines the teaserText of the text part.
       */
      teaserText: {
        type: String,
        default: null,
      },

      /**
       * Allows to define a link for the banner.
       */
      link: {
        type: Object,
        default: null,
      },

      /**
       * Allows to customize or disable the icon right next to the link text.
       */
      linkIcon: {
        type: String,
        default: 'i-chevron-2--right',
      },

      /**
       * Allows to define a link to an overview page.
       */
      overviewLink: {
        type: Object,
        default: null,
      },

      /**
       * Allows to define an initial width for the image.
       */
      imageInitialWidth: {
        type: Number,
        default: 1600,
      },

      /**
       * Allows to define an initial height for the image.
       */
      imageInitialHeight: {
        type: Number,
        default: 1200,
      },

      /**
       * Gets the date of the blog entry.
       */
      date: {
        type: String,
        default: null,
      },

      /**
       * Allows to change the width of the component.
       *
       * - fullwidth - The component uses 100vw.
       * - auto - The component uses the width of the parent.
       */
      width: {
        type: String,
        default: 'fullwidth',
        validator(value) {
          return ['fullwidth', 'auto'].includes(value);
        },
      },

      /**
       * Allows to select the layout variant for the component.
       */
      variant: {
        type: String,
        default: 'default',
        validator(value) {
          return [
            'default',
            'header', // eIFU
          ].includes(value);
        },
      },
    },
    data() {
      return {
        /**
         * @type {Object} Defines the sizes per viewport for the image.
         */
        sizes: {
          lg: 1600,
          sm: 500,
        },
      };
    },

    computed: {
      /**
       * Determines the final video to be displayed on tablet, falling back to desktop if not available.
       * @type {Object}
       * @returns {Object} Video object for tablet.
       */
      finalTabletVideo() {
        return this.tabletVideo.path ? this.tabletVideo : this.desktopVideo;
      },

      /**
       * Determines the final video to be displayed on mobile, falling back to desktop if not available.
       * @type {Object}
       * @returns {Object} Video object for mobile.
       */
      finalMobileVideo() {
        return this.mobileVideo.path ? this.mobileVideo : this.desktopVideo;
      },

      /**
       * Returns a prefix string.
       *
       * @returns {String}
       */
      prefixLine() {
        return [this.prefix, this.date ? formatDate(this.date) : ''].filter(Boolean).join(' ');
      },
    },
    // watch: {},

    // beforeCreate() {},
    // created() {},
    // beforeMount() {},
    // mounted() {},
    // beforeUpdate() {},
    // updated() {},
    // activated() {},
    // deactivated() {},
    // beforeUnmount() {},
    // unmounted() {},

    // methods: {},
    // render() {},
  };
</script>

<style lang="scss">
  @use '@/setup/scss/variables';
  @use '@/setup/scss/mixins';
  @use '@/setup/scss/functions';
  @use '@/setup/scss/extends' as *;

  .c-hero-banner {
    $this: &;
    $grid-area--prefix: prefix;
    $grid-area--title: title;
    $grid-area--text: text;
    $grid-area--link: link;

    position: relative;
    padding-top: functions.rem(variables.$spacing--50);

    @include mixins.media(sm) {
      padding: functions.rem(variables.$spacing--80 0);
    }

    &__container {
      @extend %border-gradient;

      position: relative;
      display: flex;
      flex-direction: column;
      justify-content: flex-end;
      margin: functions.rem(0 variables.$grid-whitespace--xs);
      padding: functions.rem(220px variables.$spacing--15 variables.$spacing--15);

      @include mixins.media(sm) {
        flex-direction: row;
        margin: functions.rem(0 variables.$grid-whitespace--md);
        padding: functions.rem(variables.$spacing--25);
      }

      @include mixins.media(md) {
        min-height: functions.rem(500px);
        margin: functions.rem(0 variables.$grid-whitespace--lg);
        padding: functions.rem(variables.$spacing--50);
      }
    }

    &__text-wrapper {
      flex: 1 0 auto;

      @include mixins.media(sm) {
        flex-basis: 50%;
        max-width: 50%;
        min-height: functions.rem(250px);
        padding-left: functions.rem(variables.$spacing--25);
      }

      @include mixins.media(md) {
        min-height: functions.rem(400px);
        padding-left: functions.rem(variables.$spacing--50);
      }

      &--flex {
        display: grid;
        grid-template-areas:
          '#{$grid-area--prefix}'
          '#{$grid-area--title}'
          '#{$grid-area--text}'
          '#{$grid-area--link}';
        grid-template-rows: auto 1fr auto auto;

        #{$this}__prefix {
          grid-area: $grid-area--prefix;
        }

        #{$this}__title {
          grid-area: $grid-area--title;
        }

        #{$this}__teaser-text {
          grid-area: $grid-area--text;
        }

        #{$this}__link {
          grid-area: $grid-area--link;
        }
      }
    }

    #{$this}__video-wrapper {
      position: absolute;
      top: 0;
      overflow: hidden;

      @include mixins.media($down: xs) {
        @include mixins.fullwidth();

        position: absolute;
      }

      @include mixins.media(sm) {
        width: 50%;
        height: 100%;
      }
    }

    &__video {
      position: relative;
      width: 100%;
      height: auto;
      overflow: hidden;

      @include mixins.deviceStyles;

      // Ratios can change based on content, so we need to assure the video is vertically centered.
      @include mixins.media(sm) {
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
      }
    }

    &__image {
      position: absolute;
      top: 0;
      height: functions.rem(250px);
      background: url('../assets/placeholder/placeholder.svg') no-repeat center center
        functions.get-theme-var('color-grayscale-920');
      background-size: 40%;

      @include mixins.media($down: xs) {
        @include mixins.fullwidth();

        position: absolute;
      }

      @include mixins.media(sm) {
        width: 50%;
        height: 100%;
      }

      .e-picture {
        position: relative;
        width: 100%;
        height: 100%;
        overflow: hidden;
      }

      img {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);

        @supports (object-fit: cover) {
          position: initial;
          top: auto;
          left: auto;
          width: 100%;
          height: 100%;
          transform: none;
          object-fit: cover;
        }
      }
    }

    &__prefix {
      margin-bottom: functions.rem(variables.$spacing--15);

      span:first-child {
        margin-right: functions.rem(variables.$spacing--25);
      }
    }

    &__title {
      @extend %heading-h2;
      @extend %hyphens;

      margin-bottom: functions.rem(variables.$spacing--20);
    }

    &__teaser-text--primary-color {
      color: functions.get-theme-var('color-primary-1');
    }

    &__link {
      display: flex;
      align-items: center;
      margin-top: functions.rem(variables.$spacing--10);
      font-weight: variables.$font-weight--semi-bold;

      @include mixins.media(md) {
        margin-top: functions.rem(variables.$spacing--25);
      }

      .e-icon-sprite {
        @include mixins.showMoreIcon();
      }
    }

    &__overview-link {
      margin: functions.rem(variables.$spacing--15 (variables.$grid-whitespace--xs + variables.$spacing--15) 0);
      color: functions.get-theme-var('color-grayscale-100');
      font-weight: variables.$font-weight--semi-bold;

      @include mixins.media(sm) {
        position: absolute;
        right: functions.rem(variables.$grid-whitespace--md);
        bottom: 0;
        height: functions.rem(variables.$spacing--80); // Keep in sync with root padding.
        margin: 0;
      }

      @include mixins.media(md) {
        right: functions.rem(variables.$grid-whitespace--lg);
      }

      .e-icon-sprite {
        @include mixins.showMoreIcon();

        display: inline-block;
        vertical-align: middle;
      }

      &:hover {
        color: functions.get-theme-var('color-primary-1');
      }
    }

    &--image-position-right {
      #{$this}__container {
        @include mixins.media(sm) {
          justify-content: flex-start;
        }
      }

      #{$this}__image {
        @include mixins.media(sm) {
          left: 50%;
        }
      }

      #{$this}__video-wrapper {
        @include mixins.media(sm) {
          left: 50%;
        }
      }

      #{$this}__text-wrapper {
        @include mixins.media(sm) {
          padding-right: functions.rem(variables.$spacing--50);
          padding-left: 0;
        }
      }

      #{$this}__overview-link {
        right: auto;
        left: functions.rem(variables.$grid-whitespace--xs);

        @include mixins.media(sm) {
          right: auto;
          left: functions.rem(variables.$grid-whitespace--md);
        }

        @include mixins.media(md) {
          right: auto;
          left: functions.rem(variables.$grid-whitespace--lg);
        }
      }
    }

    &--image-position-top {
      display: flex;
      flex-direction: column;
      padding-top: 0;

      #{$this}__image {
        position: relative;
        display: block;
        width: auto;
        height: 100%;
        max-height: functions.rem(600px);
        overflow: hidden;

        @include mixins.media(xl) {
          margin-right: functions.rem(-1 * variables.$grid-whitespace--lg);
          margin-left: functions.rem(-1 * variables.$grid-whitespace--lg);
        }

        & img {
          max-height: functions.rem(600px);
        }
      }

      #{$this}__container {
        justify-content: flex-start;
        min-height: auto;
      }

      #{$this}__text-wrapper {
        flex: 1;
        justify-content: flex-start;
        max-width: none;
        min-height: auto;
        padding-left: 0;
      }
    }

    &--width-fullwidth {
      @extend %fullwidth--content;
    }

    &--width-auto {
      padding-right: 0;
      padding-left: 0;
    }

    &--variant-header {
      #{$this}__teaser-text {
        @include mixins.font(variables.$font-size--16);

        color: functions.get-theme-var('color-primary-1');

        @include mixins.media(md) {
          @include mixins.font(variables.$font-size--18);
        }

        @include mixins.media(lg) {
          @include mixins.font-size(variables.$font-size--18, variables.$font-size--lg-scale-basis);
        }
      }
    }
  }
</style>
